import {
  ArrowLeft,
  ArrowRight,
  BadgeCheck,
  Check,
  CreditCard,
  FileText,
  LoaderCircle,
  LockKeyhole,
  Mail,
  Phone,
  ReceiptText,
  ShieldCheck,
  UserRound
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { getPlan, getPlanCharge, getPlanPrice, plans, type BillingInterval, type PlanId } from '../data/plans';
import { SelectMenu } from '../components/SelectMenu';
import { useAuth } from '../hooks/useAuth';
import { createCheckout, type BillingDetails } from '../lib/api';
import { Seo } from '../lib/seo';

const countries = [
  ["US", "United States"],
  ["GB", "United Kingdom"],
  ["CA", "Canada"],
  ["AU", "Australia"],
  ["LK", "Sri Lanka"],
  ["IN", "India"],
  ["SG", "Singapore"],
  ["AE", "United Arab Emirates"],
  ["DE", "Germany"],
  ["FR", "France"],
  ["NL", "Netherlands"],
  ["IT", "Italy"],
  ["ES", "Spain"],
  ["PT", "Portugal"],
  ["SE", "Sweden"],
  ["NO", "Norway"],
  ["DK", "Denmark"],
  ["FI", "Finland"],
  ["CH", "Switzerland"],
  ["AT", "Austria"],
  ["BE", "Belgium"],
  ["IE", "Ireland"],
  ["NZ", "New Zealand"],
  ["JP", "Japan"],
  ["KR", "South Korea"],
  ["MY", "Malaysia"],
  ["PH", "Philippines"],
  ["ID", "Indonesia"],
  ["TH", "Thailand"],
  ["BR", "Brazil"],
  ["MX", "Mexico"],
  ["ZA", "South Africa"],
  ["AF", "Afghanistan"],
  ["AL", "Albania"],
  ["DZ", "Algeria"],
  ["AS", "American Samoa"],
  ["AD", "Andorra"],
  ["AO", "Angola"],
  ["AI", "Anguilla"],
  ["AQ", "Antarctica"],
  ["AG", "Antigua and Barbuda"],
  ["AR", "Argentina"],
  ["AM", "Armenia"],
  ["AW", "Aruba"],
  ["AZ", "Azerbaijan"],
  ["BS", "Bahamas"],
  ["BH", "Bahrain"],
  ["BD", "Bangladesh"],
  ["BB", "Barbados"],
  ["BY", "Belarus"],
  ["BZ", "Belize"],
  ["BJ", "Benin"],
  ["BM", "Bermuda"],
  ["BT", "Bhutan"],
  ["BO", "Bolivia"],
  ["BQ", "Bonaire, Sint Eustatius and Saba"],
  ["BA", "Bosnia and Herzegovina"],
  ["BW", "Botswana"],
  ["BV", "Bouvet Island"],
  ["IO", "British Indian Ocean Territory"],
  ["BN", "Brunei"],
  ["BG", "Bulgaria"],
  ["BF", "Burkina Faso"],
  ["BI", "Burundi"],
  ["CV", "Cabo Verde"],
  ["KH", "Cambodia"],
  ["CM", "Cameroon"],
  ["KY", "Cayman Islands"],
  ["CF", "Central African Republic"],
  ["TD", "Chad"],
  ["CL", "Chile"],
  ["CN", "China"],
  ["CX", "Christmas Island"],
  ["CC", "Cocos (Keeling) Islands"],
  ["CO", "Colombia"],
  ["KM", "Comoros"],
  ["CG", "Congo"],
  ["CD", "Congo (Democratic Republic)"],
  ["CK", "Cook Islands"],
  ["CR", "Costa Rica"],
  ["HR", "Croatia"],
  ["CU", "Cuba"],
  ["CW", "Curaçao"],
  ["CY", "Cyprus"],
  ["CZ", "Czechia"],
  ["CI", "Côte d’Ivoire"],
  ["DJ", "Djibouti"],
  ["DM", "Dominica"],
  ["DO", "Dominican Republic"],
  ["EC", "Ecuador"],
  ["EG", "Egypt"],
  ["SV", "El Salvador"],
  ["GQ", "Equatorial Guinea"],
  ["ER", "Eritrea"],
  ["EE", "Estonia"],
  ["SZ", "Eswatini"],
  ["ET", "Ethiopia"],
  ["FK", "Falkland Islands (Malvinas)"],
  ["FO", "Faroe Islands"],
  ["FJ", "Fiji"],
  ["GF", "French Guiana"],
  ["PF", "French Polynesia"],
  ["TF", "French Southern Territories"],
  ["GA", "Gabon"],
  ["GM", "Gambia"],
  ["GE", "Georgia"],
  ["GH", "Ghana"],
  ["GI", "Gibraltar"],
  ["GR", "Greece"],
  ["GL", "Greenland"],
  ["GD", "Grenada"],
  ["GP", "Guadeloupe"],
  ["GU", "Guam"],
  ["GT", "Guatemala"],
  ["GG", "Guernsey"],
  ["GN", "Guinea"],
  ["GW", "Guinea-Bissau"],
  ["GY", "Guyana"],
  ["HT", "Haiti"],
  ["HM", "Heard Island and McDonald Islands"],
  ["HN", "Honduras"],
  ["HK", "Hong Kong"],
  ["HU", "Hungary"],
  ["IS", "Iceland"],
  ["IR", "Iran"],
  ["IQ", "Iraq"],
  ["IM", "Isle of Man"],
  ["IL", "Israel"],
  ["JM", "Jamaica"],
  ["JE", "Jersey"],
  ["JO", "Jordan"],
  ["KZ", "Kazakhstan"],
  ["KE", "Kenya"],
  ["KI", "Kiribati"],
  ["KW", "Kuwait"],
  ["KG", "Kyrgyzstan"],
  ["LA", "Laos"],
  ["LV", "Latvia"],
  ["LB", "Lebanon"],
  ["LS", "Lesotho"],
  ["LR", "Liberia"],
  ["LY", "Libya"],
  ["LI", "Liechtenstein"],
  ["LT", "Lithuania"],
  ["LU", "Luxembourg"],
  ["MO", "Macao"],
  ["MG", "Madagascar"],
  ["MW", "Malawi"],
  ["MV", "Maldives"],
  ["ML", "Mali"],
  ["MT", "Malta"],
  ["MH", "Marshall Islands"],
  ["MQ", "Martinique"],
  ["MR", "Mauritania"],
  ["MU", "Mauritius"],
  ["YT", "Mayotte"],
  ["FM", "Micronesia"],
  ["MD", "Moldova"],
  ["MC", "Monaco"],
  ["MN", "Mongolia"],
  ["ME", "Montenegro"],
  ["MS", "Montserrat"],
  ["MA", "Morocco"],
  ["MZ", "Mozambique"],
  ["MM", "Myanmar"],
  ["NA", "Namibia"],
  ["NR", "Nauru"],
  ["NP", "Nepal"],
  ["NC", "New Caledonia"],
  ["NI", "Nicaragua"],
  ["NE", "Niger"],
  ["NG", "Nigeria"],
  ["NU", "Niue"],
  ["NF", "Norfolk Island"],
  ["KP", "North Korea"],
  ["MK", "North Macedonia"],
  ["MP", "Northern Mariana Islands"],
  ["OM", "Oman"],
  ["PK", "Pakistan"],
  ["PW", "Palau"],
  ["PS", "Palestine"],
  ["PA", "Panama"],
  ["PG", "Papua New Guinea"],
  ["PY", "Paraguay"],
  ["PE", "Peru"],
  ["PN", "Pitcairn"],
  ["PL", "Poland"],
  ["PR", "Puerto Rico"],
  ["QA", "Qatar"],
  ["RO", "Romania"],
  ["RU", "Russia"],
  ["RW", "Rwanda"],
  ["RE", "Réunion"],
  ["BL", "Saint Barthélemy"],
  ["SH", "Saint Helena, Ascension and Tristan da Cunha"],
  ["KN", "Saint Kitts and Nevis"],
  ["LC", "Saint Lucia"],
  ["MF", "Saint Martin (French part)"],
  ["PM", "Saint Pierre and Miquelon"],
  ["VC", "Saint Vincent and the Grenadines"],
  ["WS", "Samoa"],
  ["SM", "San Marino"],
  ["ST", "Sao Tome and Principe"],
  ["SA", "Saudi Arabia"],
  ["SN", "Senegal"],
  ["RS", "Serbia"],
  ["SC", "Seychelles"],
  ["SL", "Sierra Leone"],
  ["SX", "Sint Maarten (Dutch part)"],
  ["SK", "Slovakia"],
  ["SI", "Slovenia"],
  ["SB", "Solomon Islands"],
  ["SO", "Somalia"],
  ["GS", "South Georgia and the South Sandwich Islands"],
  ["SS", "South Sudan"],
  ["SD", "Sudan"],
  ["SR", "Suriname"],
  ["SJ", "Svalbard and Jan Mayen"],
  ["SY", "Syria"],
  ["TW", "Taiwan"],
  ["TJ", "Tajikistan"],
  ["TZ", "Tanzania"],
  ["TL", "Timor-Leste"],
  ["TG", "Togo"],
  ["TK", "Tokelau"],
  ["TO", "Tonga"],
  ["TT", "Trinidad and Tobago"],
  ["TN", "Tunisia"],
  ["TM", "Turkmenistan"],
  ["TC", "Turks and Caicos Islands"],
  ["TV", "Tuvalu"],
  ["TR", "Türkiye"],
  ["UG", "Uganda"],
  ["UA", "Ukraine"],
  ["UM", "United States Minor Outlying Islands"],
  ["UY", "Uruguay"],
  ["UZ", "Uzbekistan"],
  ["VU", "Vanuatu"],
  ["VA", "Vatican City"],
  ["VE", "Venezuela"],
  ["VN", "Vietnam"],
  ["VG", "Virgin Islands, British"],
  ["VI", "Virgin Islands, U.S."],
  ["WF", "Wallis and Futuna"],
  ["EH", "Western Sahara"],
  ["YE", "Yemen"],
  ["ZM", "Zambia"],
  ["ZW", "Zimbabwe"],
  ["AX", "Åland Islands"]
] as const;

const checkoutDraftKey = (userId: string) => `nexhance-checkout-draft:${userId}`;

const emptyDetails: BillingDetails = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  company: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  region: '',
  postalCode: '',
  country: 'US',
  taxNumber: ''
};

export function CheckoutPage() {
  const [params, setParams] = useSearchParams();
  const { user, session, loading } = useAuth();
  const requestedPlan = params.get('plan');
  const plan = getPlan(requestedPlan);
  const billing: BillingInterval = params.get('billing') === 'yearly' ? 'yearly' : 'monthly';
  const [details, setDetails] = useState<BillingDetails>(emptyDetails);
  const [accepted, setAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!user) return;
    try {
      const saved = localStorage.getItem(checkoutDraftKey(user.uid));
      const draft = saved ? (JSON.parse(saved) as Partial<BillingDetails>) : {};
      setDetails({ ...emptyDetails, ...draft, email: user.email ?? '' });
    } catch {
      setDetails({ ...emptyDetails, email: user.email ?? '' });
    }
  }, [user]);

  useEffect(() => {
    if (!user) return;
    localStorage.setItem(checkoutDraftKey(user.uid), JSON.stringify({ ...details, email: user.email ?? details.email }));
  }, [details, user]);

  const charge = getPlanCharge(plan, billing);
  const monthlyEquivalent = getPlanPrice(plan, billing);
  const annualSaving = useMemo(() => plan.monthlyPrice * 12 - plan.yearlyMonthlyPrice * 12, [plan]);

  if (loading) return <div className="page-loader">Preparing secure checkout…</div>;
  if (!user) {
    const next = encodeURIComponent(`/checkout?plan=${plan.id}&billing=${billing}`);
    return <Navigate to={`/auth?next=${next}`} replace />;
  }

  function setBilling(next: BillingInterval) {
    const nextParams = new URLSearchParams(params);
    nextParams.set('plan', plan.id);
    nextParams.set('billing', next);
    setParams(nextParams, { replace: true });
  }

  function setPlan(nextPlan: PlanId) {
    const nextParams = new URLSearchParams(params);
    nextParams.set('plan', nextPlan);
    nextParams.set('billing', billing);
    setParams(nextParams, { replace: true });
  }

  function updateField<K extends keyof BillingDetails>(key: K, value: BillingDetails[K]) {
    setDetails((current) => ({ ...current, [key]: value }));
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError('');
    if (!user) {
      setError('Sign in before continuing to payment.');
      return;
    }
    if (!accepted) {
      setError('Confirm the subscription terms and refund policy before continuing.');
      return;
    }
    setSubmitting(true);
    try {
      const result = await createCheckout(plan.id, billing, details, session?.access_token);
      localStorage.removeItem(checkoutDraftKey(user.uid));
      window.location.href = result.url;
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not prepare the secure checkout.');
      setSubmitting(false);
    }
  }

  return (
    <section className="page-section checkout-page">
      <Seo title={`Checkout - ${plan.name} Plan`} description="Complete your Nexhance AI billing details before continuing to secure hosted payment." path="/checkout" noindex />
      <div className="shell checkout-shell">
        <div className="checkout-topbar">
          <Link to="/pricing"><ArrowLeft size={17} /> Back to pricing</Link>
          <ol className="checkout-progress" aria-label="Checkout progress">
            <li className="complete">
              <span className="checkout-progress-marker"><Check size={14} aria-hidden="true" /></span>
              <span className="checkout-progress-copy"><strong>Plan</strong><small>Selected</small></span>
            </li>
            <li className="active" aria-current="step">
              <span className="checkout-progress-marker">2</span>
              <span className="checkout-progress-copy"><strong>Billing details</strong><small>Current step</small></span>
            </li>
            <li>
              <span className="checkout-progress-marker">3</span>
              <span className="checkout-progress-copy"><strong>Secure payment</strong><small>Dodo checkout</small></span>
            </li>
          </ol>
        </div>

        <div className="checkout-layout">
          <form className="checkout-form" onSubmit={submit}>
            <header className="checkout-heading">
              <p className="eyebrow">Billing details</p>
              <h1>Confirm who the subscription is for.</h1>
              <p>Your card details are entered on Dodo Payments’ secure hosted checkout after this step.</p>
            </header>

            <section className="checkout-section">
              <div className="checkout-section-heading"><span><UserRound size={19} /></span><div><h2>Customer information</h2><p>Used for the order record, receipt and billing support.</p></div></div>
              <div className="checkout-fields two-column">
                <label><span>First name</span><input value={details.firstName} onChange={(event) => updateField('firstName', event.target.value)} autoComplete="given-name" minLength={2} maxLength={50} required /></label>
                <label><span>Last name</span><input value={details.lastName} onChange={(event) => updateField('lastName', event.target.value)} autoComplete="family-name" minLength={1} maxLength={50} required /></label>
                <label className="full"><span>Email address</span><div className="input-with-icon"><Mail size={17} /><input type="email" value={details.email} readOnly disabled /></div><small>Receipts and account notifications are sent to your signed-in email.</small></label>
                <label><span>Phone number <em>Optional</em></span><div className="input-with-icon"><Phone size={17} /><input type="tel" value={details.phone} onChange={(event) => updateField('phone', event.target.value)} autoComplete="tel" placeholder="+1 415 555 0136" maxLength={30} /></div></label>
                <label><span>Company or studio <em>Optional</em></span><input value={details.company} onChange={(event) => updateField('company', event.target.value)} autoComplete="organization" maxLength={80} /></label>
              </div>
            </section>

            <section className="checkout-section">
              <div className="checkout-section-heading"><span><ReceiptText size={19} /></span><div><h2>Billing address</h2><p>Helps prepare accurate tax and invoice details at the hosted checkout.</p></div></div>
              <div className="checkout-fields two-column">
                <label className="full"><span>Address line 1</span><input value={details.addressLine1} onChange={(event) => updateField('addressLine1', event.target.value)} autoComplete="address-line1" minLength={4} maxLength={100} required /></label>
                <label className="full"><span>Address line 2 <em>Optional</em></span><input value={details.addressLine2} onChange={(event) => updateField('addressLine2', event.target.value)} autoComplete="address-line2" maxLength={100} /></label>
                <label><span>City</span><input value={details.city} onChange={(event) => updateField('city', event.target.value)} autoComplete="address-level2" minLength={2} maxLength={70} required /></label>
                <label><span>State / province <em>Optional</em></span><input value={details.region} onChange={(event) => updateField('region', event.target.value)} autoComplete="address-level1" maxLength={70} /></label>
                <label><span>Postal code</span><input value={details.postalCode} onChange={(event) => updateField('postalCode', event.target.value)} autoComplete="postal-code" minLength={2} maxLength={20} required /></label>
                <label><span>Country</span><SelectMenu ariaLabel="Country" value={details.country} onChange={(next) => updateField('country', next)} choices={countries.map(([code, name]) => ({ value: code, label: name }))} /></label>
                <label className="full"><span>Tax / VAT number <em>Optional</em></span><input value={details.taxNumber} onChange={(event) => updateField('taxNumber', event.target.value)} maxLength={50} placeholder="For eligible business invoices" /></label>
              </div>
            </section>

            <label className="checkout-consent">
              <input type="checkbox" checked={accepted} onChange={(event) => setAccepted(event.target.checked)} />
              <span>I understand this is a recurring {billing} subscription and agree to the <Link to="/terms" target="_blank">Terms of Service</Link>, <Link to="/privacy" target="_blank">Privacy Policy</Link> and <Link to="/refund-policy" target="_blank">Refund Policy</Link>.</span>
            </label>

            {error && <div className="inline-error checkout-error">{error}</div>}
            <button className="button button-primary button-large checkout-submit" disabled={submitting}>
              {submitting ? <><LoaderCircle size={18} className="spin" /> Preparing checkout…</> : <><LockKeyhole size={18} /> Continue to secure payment <ArrowRight size={18} /></>}
            </button>
            <div className="checkout-security-note"><ShieldCheck size={17} /><span>Nexhance AI never receives or stores your complete payment-card number.</span></div>
          </form>

          <aside className="order-summary">
            <div className="order-summary-sticky">
              <p className="eyebrow">Order summary</p>
              <div className="order-plan-title"><div><h2>{plan.name}</h2><span>{plan.audience}</span></div><button type="button" onClick={() => document.getElementById('plan-selector')?.scrollIntoView({ behavior: 'smooth' })}>Change</button></div>
              <div className="order-price"><strong>${charge}</strong><span>USD / {billing === 'yearly' ? 'year' : 'month'}</span></div>
              {billing === 'yearly' && <div className="saving-note">Equivalent to ${monthlyEquivalent}/month · Save ${annualSaving}/year</div>}
              <div className="order-credit"><span>{(billing === 'yearly' ? plan.credits * 12 : plan.credits).toLocaleString()}</span><div><strong>{billing === 'yearly' ? 'credits for the annual term' : 'credits each monthly term'}</strong><small>{billing === 'yearly' ? 'Granted upfront after annual payment' : 'Balance resets after successful renewal'}</small></div></div>
              <ul>{plan.features.slice(0, 5).map((feature) => <li key={feature}><Check size={16} /> {feature}</li>)}</ul>
              <div className="order-breakdown"><div><span>Subscription</span><strong>${charge}.00</strong></div><div><span>Tax</span><strong>Calculated securely</strong></div><div className="total"><span>Due at payment</span><strong>${charge}.00 + tax</strong></div></div>
              <div className="order-assurance"><BadgeCheck size={19} /><div><strong>Failed jobs restore credits</strong><span>When a processing run fails before returning a usable result.</span></div></div>
            </div>
          </aside>
        </div>

        <section className="checkout-plan-selector" id="plan-selector">
          <div><p className="eyebrow">Change selection</p><h2>Choose a different plan or billing interval</h2></div>
          <div className="compact-plan-grid">{plans.map((item) => <button type="button" key={item.id} className={item.id === plan.id ? 'active' : ''} onClick={() => setPlan(item.id)}><span>{item.name}</span><strong>${billing === 'yearly' ? item.yearlyMonthlyPrice : item.monthlyPrice}/mo</strong><small>{(billing === 'yearly' ? item.credits * 12 : item.credits).toLocaleString()} credits {billing === 'yearly' ? '/ year' : '/ month'}</small></button>)}</div>
          <div className="billing-toggle checkout-billing-toggle"><button type="button" className={billing === 'monthly' ? 'active' : ''} onClick={() => setBilling('monthly')}>Monthly</button><button type="button" className={billing === 'yearly' ? 'active' : ''} onClick={() => setBilling('yearly')}>Yearly <span>Save about 20%</span></button></div>
        </section>

        <div className="checkout-bottom-trust"><span><CreditCard size={17} /> Secure hosted card collection</span><span><FileText size={17} /> Email receipt and invoice details</span><span><ShieldCheck size={17} /> Server-verified subscription activation</span></div>
      </div>
    </section>
  );
}
